import { Fragment } from "react"
import type { ReactNode } from "react"
import Tooltip from "@/components/Tooltip/Tooltip"
import styles from "@/styles/components/SegmentedControl.module.css"

/**
 * A row of pills where exactly one is chosen.
 *
 * The same object as the phase row in the session header, the filter row above
 * a list and the list/grid toggle — they were three separate implementations of
 * one control, which is how they drifted apart.
 *
 * Generic over the option value so a caller keeps its own union: the phase row
 * passes `Phase`, the filters pass `StateFilter`, and neither has to widen to
 * `string` and cast back.
 */

export interface Segment<T extends string> {
  value: T
  label: ReactNode
  /**
   * The bubble on hover, and the name the pill is announced by. Required when
   * the label is a glyph, and worth setting when a pill is `locked` — that is
   * the only place the reason can be read.
   */
  title?: string
  /** Unavailable, and takes no pointer events at all. */
  disabled?: boolean
  /**
   * Present but refused: greyed like `disabled`, and still hoverable so its
   * `title` can say why. A truly disabled button dispatches no pointer events,
   * which would silence the tooltip on exactly the pills that need one.
   */
  locked?: boolean
}

interface SegmentedControlProps<T extends string> {
  options: readonly Segment<T>[]
  /** Null lights nothing — a real state for the phase row, where the room can
   *  stand on a phase that has no chip of its own. */
  value: T | null
  onChange: (value: T) => void
  /** What the group is, for a screen reader. */
  label: string
  /** `track` carries the pale rail behind the pills; `loose` lets them float. */
  variant?: "track" | "loose"
  size?: "sm" | "md"
}

export default function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  label,
  variant = "track",
  size = "md",
}: SegmentedControlProps<T>) {
  return (
    <div
      className={[styles.group, styles[variant], styles[size]].join(" ")}
      role="group"
      aria-label={label}
    >
      {options.map((option) => {
        const pill = (
          <button
            type="button"
            className={[
              styles.item,
              option.value === value ? styles.active : "",
              option.locked ? styles.locked : "",
            ]
              .filter(Boolean)
              .join(" ")}
            aria-pressed={option.value === value}
            aria-disabled={option.locked || undefined}
            // The bubble carries the name, so it must not be announced twice.
            aria-label={option.title}
            disabled={option.disabled}
            onClick={() => {
              if (option.locked) return
              onChange(option.value)
            }}
          >
            {option.label}
          </button>
        )

        return option.title ? (
          <Tooltip key={option.value} label={option.title}>
            {pill}
          </Tooltip>
        ) : (
          // No wrapper without a bubble to hang on it: the pills are flex items
          // of the track, and an extra box per pill would be one more thing that
          // has to be told not to change the row.
          <Fragment key={option.value}>{pill}</Fragment>
        )
      })}
    </div>
  )
}
